import { defineMessages, type MessageDescriptor } from 'react-intl';

/**
 * Curated, finite greeting sets keyed by personality tone and chat mode. Copy is
 * placeholder from content design (Rovo Personality — Chat greetings) and is
 * structured so the final strings can be swapped in per (tone, mode) without
 * touching selection logic. `{name}` is the user's first name.
 *
 * Keys are `<tone><Mode><n>`; tones map to `RovoResponseTone` (`collaborative` =
 * `rovo_default`) and modes to the `RovoGreeting` branches (`Normal` =
 * auto / quick answers, `ThinkDeeper` = think deeper, `Task` = work).
 * `greetingSets` groups these into per-(mode, tone) arrays by key prefix.
 */
type MessageKeys =
	| 'collaborativeNormal1'
	| 'collaborativeNormal2'
	| 'collaborativeNormal3'
	| 'collaborativeNormal4'
	| 'collaborativeNormal5'
	| 'collaborativeNormal6'
	| 'collaborativeNormal7'
	| 'collaborativeNormal8'
	| 'collaborativeNormal9'
	| 'collaborativeNormal10'
	| 'collaborativeNormal11'
	| 'collaborativeThinkDeeper1'
	| 'collaborativeThinkDeeper2'
	| 'collaborativeThinkDeeper3'
	| 'collaborativeThinkDeeper4'
	| 'collaborativeThinkDeeper5'
	| 'collaborativeThinkDeeper6'
	| 'collaborativeThinkDeeper7'
	| 'collaborativeThinkDeeper8'
	| 'collaborativeThinkDeeper9'
	| 'collaborativeThinkDeeper10'
	| 'collaborativeTask1'
	| 'collaborativeTask2'
	| 'collaborativeTask3'
	| 'collaborativeTask4'
	| 'collaborativeTask5'
	| 'collaborativeTask6'
	| 'collaborativeTask7'
	| 'professionalNormal1'
	| 'professionalNormal2'
	| 'professionalNormal3'
	| 'professionalNormal4'
	| 'professionalNormal5'
	| 'professionalNormal6'
	| 'professionalThinkDeeper1'
	| 'professionalThinkDeeper2'
	| 'professionalThinkDeeper3'
	| 'professionalThinkDeeper4'
	| 'professionalThinkDeeper5'
	| 'professionalThinkDeeper6'
	| 'professionalThinkDeeper7'
	| 'professionalTask1'
	| 'professionalTask2'
	| 'professionalTask3'
	| 'professionalTask4'
	| 'professionalTask5'
	| 'professionalTask6'
	| 'professionalTask7'
	| 'professionalTask8'
	| 'creativeNormal1'
	| 'creativeNormal2'
	| 'creativeNormal3'
	| 'creativeNormal4'
	| 'creativeNormal5'
	| 'creativeNormal6'
	| 'creativeNormal7'
	| 'creativeNormal8'
	| 'creativeNormal9'
	| 'creativeThinkDeeper1'
	| 'creativeThinkDeeper2'
	| 'creativeThinkDeeper3'
	| 'creativeThinkDeeper4'
	| 'creativeThinkDeeper5'
	| 'creativeThinkDeeper6'
	| 'creativeTask1'
	| 'creativeTask2'
	| 'creativeTask3'
	| 'creativeTask4'
	| 'creativeTask5'
	| 'creativeTask6'
	| 'empatheticNormal1'
	| 'empatheticNormal2'
	| 'empatheticNormal3'
	| 'empatheticNormal4'
	| 'empatheticNormal5'
	| 'empatheticNormal6'
	| 'empatheticNormal7'
	| 'empatheticThinkDeeper1'
	| 'empatheticThinkDeeper2'
	| 'empatheticThinkDeeper3'
	| 'empatheticThinkDeeper4'
	| 'empatheticThinkDeeper5'
	| 'empatheticThinkDeeper6'
	| 'empatheticTask1'
	| 'empatheticTask2'
	| 'empatheticTask3'
	| 'empatheticTask4'
	| 'empatheticTask5'
	| 'empatheticTask6'
	| 'empatheticTask7'
	| 'empatheticTask8';

type DuplicateMessageKeys =
	| 'collaborativeThinkDeeper3'
	| 'collaborativeThinkDeeper4'
	| 'collaborativeThinkDeeper5'
	| 'professionalTask6'
	| 'creativeThinkDeeper4'
	| 'empatheticNormal5';

type UniqueMessageKeys = Exclude<MessageKeys, DuplicateMessageKeys>;

const uniquePersonalityGreetingMessages: Record<UniqueMessageKeys, MessageDescriptor> =
	defineMessages({
		collaborativeNormal1: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal1',
			defaultMessage: "Let's get to work, {name}",
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal2: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal2',
			defaultMessage: "What's your top priority, {name}?",
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal3: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal3',
			defaultMessage: 'What should we unblock today?',
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal4: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal4',
			defaultMessage: 'Where should we focus first, {name}?',
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal5: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal5',
			defaultMessage: "What's top of mind, {name}?",
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal6: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal6',
			defaultMessage: "Let's get things organized",
			description: 'Collaborative-tone chat greeting shared across reasoning modes.',
		},
		collaborativeNormal7: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal7',
			defaultMessage: 'How can I help, {name}?',
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal8: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal8',
			defaultMessage: "Let's dive into your work together, {name}",
			description: 'Collaborative-tone chat greeting shared across reasoning modes.',
		},
		collaborativeNormal9: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal9',
			defaultMessage: "Let's tackle your tasks together, {name}",
			description: 'Collaborative-tone chat greeting shared across reasoning modes.',
		},
		collaborativeNormal10: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal10',
			defaultMessage: 'What needs organizing, {name}?',
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},
		collaborativeNormal11: {
			id: 'rovo-personalization.personality-greeting.collaborativeNormal11',
			defaultMessage: "Let's find what matters most, {name}",
			description: 'Collaborative-tone chat greeting (normal / quick answers).',
		},

		collaborativeThinkDeeper1: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper1',
			defaultMessage: "Let's put your work into context",
			description: 'Collaborative-tone chat greeting (think deeper).',
		},
		collaborativeThinkDeeper2: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper2',
			defaultMessage: "Let's plan things out, {name}",
			description: 'Collaborative-tone chat greeting (think deeper).',
		},
		collaborativeThinkDeeper6: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper6',
			defaultMessage: 'What should we unpack together?',
			description: 'Collaborative-tone chat greeting (think deeper).',
		},
		collaborativeThinkDeeper7: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper7',
			defaultMessage: 'What needs a deeper look today?',
			description: 'Collaborative-tone chat greeting (think deeper).',
		},
		collaborativeThinkDeeper8: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper8',
			defaultMessage: "Let's connect all the dots, {name}",
			description: 'Think-deeper chat greeting shared across personality tones.',
		},
		collaborativeThinkDeeper9: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper9',
			defaultMessage: 'What should we investigate first?',
			description: 'Collaborative-tone chat greeting (think deeper).',
		},
		collaborativeThinkDeeper10: {
			id: 'rovo-personalization.personality-greeting.collaborativeThinkDeeper10',
			defaultMessage: "Let's reason through things together, {name}",
			description: 'Collaborative-tone chat greeting (think deeper).',
		},

		collaborativeTask1: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask1',
			defaultMessage: "Let's build a plan together, {name}",
			description: 'Collaborative-tone chat greeting (task mode).',
		},
		collaborativeTask2: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask2',
			defaultMessage: 'What should we create today?',
			description: 'Task-mode chat greeting shared across personality tones.',
		},
		collaborativeTask3: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask3',
			defaultMessage: "Let's map things out step by step",
			description: 'Collaborative-tone chat greeting (task mode).',
		},
		collaborativeTask4: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask4',
			defaultMessage: "Let's come up with a plan",
			description: 'Collaborative-tone chat greeting (task mode).',
		},
		collaborativeTask5: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask5',
			defaultMessage: "Let's develop some ideas together, {name}",
			description: 'Collaborative-tone chat greeting (task mode).',
		},
		collaborativeTask6: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask6',
			defaultMessage: 'Ready to turn your ideas into action?',
			description: 'Collaborative-tone chat greeting (task mode).',
		},
		collaborativeTask7: {
			id: 'rovo-personalization.personality-greeting.collaborativeTask7',
			defaultMessage: "Let's organize a path forward",
			description: 'Collaborative-tone chat greeting (task mode).',
		},

		professionalNormal1: {
			id: 'rovo-personalization.personality-greeting.professionalNormal1',
			defaultMessage: 'Ready to get to work, {name}?',
			description: 'Professional-tone chat greeting (normal / quick answers).',
		},
		professionalNormal2: {
			id: 'rovo-personalization.personality-greeting.professionalNormal2',
			defaultMessage: 'What are we working on today?',
			description: 'Professional-tone chat greeting (normal / quick answers).',
		},
		professionalNormal3: {
			id: 'rovo-personalization.personality-greeting.professionalNormal3',
			defaultMessage: "Let's get the team aligned, {name}",
			description: 'Professional-tone chat greeting (normal / quick answers).',
		},
		professionalNormal4: {
			id: 'rovo-personalization.personality-greeting.professionalNormal4',
			defaultMessage: "What's on your to-do list, {name}?",
			description: 'Professional-tone chat greeting (normal / quick answers).',
		},
		professionalNormal5: {
			id: 'rovo-personalization.personality-greeting.professionalNormal5',
			defaultMessage: 'What should we work through first?',
			description: 'Professional-tone chat greeting (normal / quick answers).',
		},
		professionalNormal6: {
			id: 'rovo-personalization.personality-greeting.professionalNormal6',
			defaultMessage: 'What are we focusing on today, {name}?',
			description: 'Professional-tone chat greeting (normal / quick answers).',
		},

		professionalThinkDeeper1: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper1',
			defaultMessage: 'What should we analyze first?',
			description: 'Professional-tone chat greeting (think deeper).',
		},
		professionalThinkDeeper2: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper2',
			defaultMessage: 'Ready for a deeper assessment, {name}?',
			description: 'Professional-tone chat greeting (think deeper).',
		},
		professionalThinkDeeper3: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper3',
			defaultMessage: 'What needs deeper analysis, {name}?',
			description: 'Professional-tone chat greeting (think deeper).',
		},
		professionalThinkDeeper4: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper4',
			defaultMessage: 'Ready to investigate deeper?',
			description: 'Professional-tone chat greeting (think deeper).',
		},
		professionalThinkDeeper5: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper5',
			defaultMessage: 'What needs a deeper review?',
			description: 'Professional-tone chat greeting (think deeper).',
		},
		professionalThinkDeeper6: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper6',
			defaultMessage: "Let's think through things systematically",
			description: 'Professional-tone chat greeting (think deeper).',
		},
		professionalThinkDeeper7: {
			id: 'rovo-personalization.personality-greeting.professionalThinkDeeper7',
			defaultMessage: 'Which details should we work through, {name}?',
			description: 'Professional-tone chat greeting (think deeper).',
		},

		professionalTask1: {
			id: 'rovo-personalization.personality-greeting.professionalTask1',
			defaultMessage: 'What should we plan out, {name}?',
			description: 'Professional-tone chat greeting (task mode).',
		},
		professionalTask2: {
			id: 'rovo-personalization.personality-greeting.professionalTask2',
			defaultMessage: "Let's work through things together, {name}",
			description: 'Chat greeting shared across personality tones and reasoning modes.',
		},
		professionalTask3: {
			id: 'rovo-personalization.personality-greeting.professionalTask3',
			defaultMessage: "Let's build out a plan",
			description: 'Professional-tone chat greeting (task mode).',
		},
		professionalTask4: {
			id: 'rovo-personalization.personality-greeting.professionalTask4',
			defaultMessage: "Let's break something complex down",
			description: 'Professional-tone chat greeting (task mode).',
		},
		professionalTask5: {
			id: 'rovo-personalization.personality-greeting.professionalTask5',
			defaultMessage: 'What requirements should we capture?',
			description: 'Professional-tone chat greeting (task mode).',
		},
		professionalTask7: {
			id: 'rovo-personalization.personality-greeting.professionalTask7',
			defaultMessage: "Let's create a path forward, {name}",
			description: 'Professional-tone chat greeting (task mode).',
		},
		professionalTask8: {
			id: 'rovo-personalization.personality-greeting.professionalTask8',
			defaultMessage: 'Ready to create a clear plan?',
			description: 'Professional-tone chat greeting (task mode).',
		},

		creativeNormal1: {
			id: 'rovo-personalization.personality-greeting.creativeNormal1',
			defaultMessage: "Let's explore some new ideas",
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal2: {
			id: 'rovo-personalization.personality-greeting.creativeNormal2',
			defaultMessage: 'What should we rethink today, {name}?',
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal3: {
			id: 'rovo-personalization.personality-greeting.creativeNormal3',
			defaultMessage: 'What do you want to tackle first?',
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal4: {
			id: 'rovo-personalization.personality-greeting.creativeNormal4',
			defaultMessage: 'Ready to plan things out?',
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal5: {
			id: 'rovo-personalization.personality-greeting.creativeNormal5',
			defaultMessage: 'What ideas should we flesh out, {name}?',
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal6: {
			id: 'rovo-personalization.personality-greeting.creativeNormal6',
			defaultMessage: 'What possibilities should we explore, {name}?',
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal7: {
			id: 'rovo-personalization.personality-greeting.creativeNormal7',
			defaultMessage: "Let's spark some new ideas together, {name}",
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal8: {
			id: 'rovo-personalization.personality-greeting.creativeNormal8',
			defaultMessage: "Let's explore your next move",
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},
		creativeNormal9: {
			id: 'rovo-personalization.personality-greeting.creativeNormal9',
			defaultMessage: "Let's create something spectacular, {name}",
			description: 'Creative-tone chat greeting (normal / quick answers).',
		},

		creativeThinkDeeper1: {
			id: 'rovo-personalization.personality-greeting.creativeThinkDeeper1',
			defaultMessage: 'What should we explore deeper, {name}?',
			description: 'Creative-tone chat greeting (think deeper).',
		},
		creativeThinkDeeper2: {
			id: 'rovo-personalization.personality-greeting.creativeThinkDeeper2',
			defaultMessage: 'What assumptions could we reframe?',
			description: 'Creative-tone chat greeting (think deeper).',
		},
		creativeThinkDeeper3: {
			id: 'rovo-personalization.personality-greeting.creativeThinkDeeper3',
			defaultMessage: "Let's uncover some new angles, {name}",
			description: 'Creative-tone chat greeting (think deeper).',
		},
		creativeThinkDeeper5: {
			id: 'rovo-personalization.personality-greeting.creativeThinkDeeper5',
			defaultMessage: "Let's think through things together, {name}",
			description: 'Creative-tone chat greeting (think deeper).',
		},
		creativeThinkDeeper6: {
			id: 'rovo-personalization.personality-greeting.creativeThinkDeeper6',
			defaultMessage: 'Which ideas should we push further?',
			description: 'Creative-tone chat greeting (think deeper).',
		},

		creativeTask1: {
			id: 'rovo-personalization.personality-greeting.creativeTask1',
			defaultMessage: 'What should we work through, {name}?',
			description: 'Creative-tone chat greeting (task mode).',
		},
		creativeTask2: {
			id: 'rovo-personalization.personality-greeting.creativeTask2',
			defaultMessage: 'What are we creating today, {name}?',
			description: 'Creative-tone chat greeting (task mode).',
		},
		creativeTask3: {
			id: 'rovo-personalization.personality-greeting.creativeTask3',
			defaultMessage: 'What idea should we develop today?',
			description: 'Creative-tone chat greeting (task mode).',
		},
		creativeTask4: {
			id: 'rovo-personalization.personality-greeting.creativeTask4',
			defaultMessage: "Let's stub out a plan, {name}",
			description: 'Creative-tone chat greeting (task mode).',
		},
		creativeTask5: {
			id: 'rovo-personalization.personality-greeting.creativeTask5',
			defaultMessage: 'Ready to make your ideas concrete, {name}?',
			description: 'Creative-tone chat greeting (task mode).',
		},
		creativeTask6: {
			id: 'rovo-personalization.personality-greeting.creativeTask6',
			defaultMessage: "Let's build something amazing, {name}",
			description: 'Creative-tone chat greeting (task mode).',
		},

		empatheticNormal1: {
			id: 'rovo-personalization.personality-greeting.empatheticNormal1',
			defaultMessage: 'What feels most pressing today?',
			description: 'Empathetic-tone chat greeting (normal / quick answers).',
		},
		empatheticNormal2: {
			id: 'rovo-personalization.personality-greeting.empatheticNormal2',
			defaultMessage: "Let's tackle something step-by-step",
			description: 'Empathetic-tone chat greeting (normal / quick answers).',
		},
		empatheticNormal3: {
			id: 'rovo-personalization.personality-greeting.empatheticNormal3',
			defaultMessage: 'Where should we start today, {name}?',
			description: 'Empathetic-tone chat greeting (normal / quick answers).',
		},
		empatheticNormal4: {
			id: 'rovo-personalization.personality-greeting.empatheticNormal4',
			defaultMessage: "What's top of mind today?",
			description: 'Empathetic-tone chat greeting (normal / quick answers).',
		},
		empatheticNormal6: {
			id: 'rovo-personalization.personality-greeting.empatheticNormal6',
			defaultMessage: "Let's take something off your plate",
			description: 'Empathetic-tone chat greeting (normal / quick answers).',
		},
		empatheticNormal7: {
			id: 'rovo-personalization.personality-greeting.empatheticNormal7',
			defaultMessage: "Let's get you unblocked, {name}",
			description: 'Empathetic-tone chat greeting (normal / quick answers).',
		},

		empatheticThinkDeeper1: {
			id: 'rovo-personalization.personality-greeting.empatheticThinkDeeper1',
			defaultMessage: "Let's look at the full picture, {name}",
			description: 'Empathetic-tone chat greeting (think deeper).',
		},
		empatheticThinkDeeper2: {
			id: 'rovo-personalization.personality-greeting.empatheticThinkDeeper2',
			defaultMessage: 'What should we think through first, {name}?',
			description: 'Empathetic-tone chat greeting (think deeper).',
		},
		empatheticThinkDeeper3: {
			id: 'rovo-personalization.personality-greeting.empatheticThinkDeeper3',
			defaultMessage: "Let's think through something complex together",
			description: 'Empathetic-tone chat greeting (think deeper).',
		},
		empatheticThinkDeeper4: {
			id: 'rovo-personalization.personality-greeting.empatheticThinkDeeper4',
			defaultMessage: 'Which concern should we examine first?',
			description: 'Empathetic-tone chat greeting (think deeper).',
		},
		empatheticThinkDeeper5: {
			id: 'rovo-personalization.personality-greeting.empatheticThinkDeeper5',
			defaultMessage: "Let's look closer at things together, {name}",
			description: 'Empathetic-tone chat greeting (think deeper).',
		},
		empatheticThinkDeeper6: {
			id: 'rovo-personalization.personality-greeting.empatheticThinkDeeper6',
			defaultMessage: 'What should we untangle together?',
			description: 'Empathetic-tone chat greeting (think deeper).',
		},

		empatheticTask1: {
			id: 'rovo-personalization.personality-greeting.empatheticTask1',
			defaultMessage: "Let's find a path forward, {name}",
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask2: {
			id: 'rovo-personalization.personality-greeting.empatheticTask2',
			defaultMessage: "Let's take things step by step, {name}",
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask3: {
			id: 'rovo-personalization.personality-greeting.empatheticTask3',
			defaultMessage: "Let's turn your ideas into a plan, {name}",
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask4: {
			id: 'rovo-personalization.personality-greeting.empatheticTask4',
			defaultMessage: "Let's break things into smaller steps, {name}",
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask5: {
			id: 'rovo-personalization.personality-greeting.empatheticTask5',
			defaultMessage: 'What can I help you think through today?',
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask6: {
			id: 'rovo-personalization.personality-greeting.empatheticTask6',
			defaultMessage: "Let's shape a practical way forward",
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask7: {
			id: 'rovo-personalization.personality-greeting.empatheticTask7',
			defaultMessage: 'What should we tackle first, {name}?',
			description: 'Empathetic-tone chat greeting (task mode).',
		},
		empatheticTask8: {
			id: 'rovo-personalization.personality-greeting.empatheticTask8',
			defaultMessage: 'Ready to work through things together?',
			description: 'Empathetic-tone chat greeting (task mode).',
		},
	});

/** Mode aliases reuse one descriptor so each shared sentence is translated once. */
export const personalityGreetingMessages: Record<MessageKeys, MessageDescriptor> = {
	...uniquePersonalityGreetingMessages,
	collaborativeThinkDeeper3: uniquePersonalityGreetingMessages.collaborativeNormal6,
	collaborativeThinkDeeper4: uniquePersonalityGreetingMessages.collaborativeNormal8,
	collaborativeThinkDeeper5: uniquePersonalityGreetingMessages.collaborativeNormal9,
	professionalTask6: uniquePersonalityGreetingMessages.collaborativeTask2,
	creativeThinkDeeper4: uniquePersonalityGreetingMessages.collaborativeThinkDeeper8,
	empatheticNormal5: uniquePersonalityGreetingMessages.professionalTask2,
};
